import { useState } from "react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useToast } from "@/components/ui/use-toast"
import { cn } from "@/lib/utils"
import { Loader2, MessageSquare, Pause, Phone, Play } from "lucide-react"
import * as outreachApi from "../api/outreach.api"
import { CHANNEL_LABEL, channelsAfterToggle, isChannelPaused } from "../lib/pause"
import { POLICY_TZ, wallTimeToIso } from "../lib/time"
import type { Channel, PauseScope, PauseState, PausedChannels } from "../types/outreach.types"

interface Props {
  scope: PauseScope
  targetId: string
  /** Who is affected, in plain words: "every patient at Best Care", "this campaign", "Maria Lopez". */
  subject: string
  pausedChannels: PausedChannels | undefined
  currentReason?: string | null
  onChange?: (state: PauseState) => void
  size?: "sm" | "default"
  disabled?: boolean
}

interface Pending {
  channel: Channel
  pausing: boolean
}

const ICON: Record<Channel, typeof Phone> = { CALL: Phone, SMS: MessageSquare }

/**
 * Two independent toggles (Texts, Calls) over POST /outreach/pause and
 * /outreach/resume (with `channels`, so one channel resumes on its own).
 */
export function ChannelPauseControl({
  scope,
  targetId,
  subject,
  pausedChannels,
  currentReason,
  onChange,
  size = "sm",
  disabled,
}: Props) {
  const { toast } = useToast()
  const [pending, setPending] = useState<Pending | null>(null)
  const [reason, setReason] = useState("")
  const [untilDate, setUntilDate] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const open = (channel: Channel) => {
    const pausing = !isChannelPaused(pausedChannels, channel)
    setReason(pausing ? "" : currentReason || "")
    setUntilDate("")
    setPending({ channel, pausing })
  }

  const confirm = async () => {
    if (!pending) return
    if (pending.pausing && !reason.trim()) return
    setSubmitting(true)
    try {
      // pause() replaces the paused set, so pausing sends the union with what
      // is already paused; resume() takes just the channel being resumed.
      const state = pending.pausing
        ? await outreachApi.pause({
            scope,
            target_id: targetId,
            channels: channelsAfterToggle(pausedChannels, pending.channel, true) || pending.channel,
            reason: reason.trim(),
            ...(untilDate ? { until: wallTimeToIso(untilDate, "00:00", POLICY_TZ) } : {}),
          })
        : await outreachApi.resume({ scope, target_id: targetId, channels: pending.channel })
      toast({
        title: `${CHANNEL_LABEL[pending.channel]} ${pending.pausing ? "paused" : "resumed"}`,
        description: subject,
      })
      onChange?.(state)
      setPending(null)
    } catch (err) {
      toast({
        title: `Could not ${pending.pausing ? "pause" : "resume"} ${CHANNEL_LABEL[pending.channel].toLowerCase()}`,
        description: outreachApi.errorMessage(err),
        variant: "destructive",
      })
    } finally {
      setSubmitting(false)
    }
  }

  const channelNoun = pending?.channel === "CALL" ? "calls" : "texts"

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {(["SMS", "CALL"] as Channel[]).map((channel) => {
          const paused = isChannelPaused(pausedChannels, channel)
          const Icon = ICON[channel]
          return (
            <Button
              key={channel}
              type="button"
              size={size}
              variant="outline"
              disabled={disabled}
              aria-pressed={paused}
              onClick={() => open(channel)}
              className={cn(
                "gap-2",
                paused && "border-warning/40 bg-warning/10 text-warning hover:bg-warning/20 hover:text-warning",
              )}
              title={paused ? `Resume ${CHANNEL_LABEL[channel].toLowerCase()}` : `Pause ${CHANNEL_LABEL[channel].toLowerCase()}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {CHANNEL_LABEL[channel]}
              <span className="flex items-center gap-1 text-xs font-normal">
                {paused ? (
                  <>
                    <Pause className="h-3 w-3" /> paused
                  </>
                ) : (
                  <>
                    <Play className="h-3 w-3" /> on
                  </>
                )}
              </span>
            </Button>
          )
        })}
      </div>

      <Dialog open={!!pending} onOpenChange={(o) => !o && !submitting && setPending(null)}>
        <DialogContent>
          {pending && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {pending.pausing ? "Pause" : "Resume"} {channelNoun} for {subject}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="space-y-2 text-sm">
                    {pending.pausing ? (
                      pending.channel === "SMS" ? (
                        <>
                          <p>
                            No texts will go out to {subject} while this is paused. Texts already
                            scheduled are skipped when they come due. They are not sent later.
                          </p>
                          <p>Calls are not affected. Patients are not told about the pause.</p>
                        </>
                      ) : (
                        <>
                          <p>
                            No calls will be placed to {subject} while this is paused. Calls already
                            booked are skipped when they come due. Bookings are not cancelled.
                          </p>
                          <p>
                            Texts are not affected. Reminder texts for a skipped call are not sent.
                            Patients are not told about the pause.
                          </p>
                        </>
                      )
                    ) : (
                      <>
                        <p>
                          {pending.channel === "SMS" ? "Texts" : "Calls"} to {subject} will start again.
                          Nothing that was skipped is re-sent or re-booked. The next{" "}
                          {pending.channel === "SMS" ? "text" : "call attempt"} goes out on the
                          campaign's normal schedule, on a weekday between 8:00 AM and 7:00 PM Central.
                        </p>
                        {isChannelPaused(pausedChannels, pending.channel === "SMS" ? "CALL" : "SMS") && (
                          <p>
                            {pending.channel === "SMS" ? "Calls" : "Texts"} stay paused.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>
              {pending.pausing && (
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label htmlFor="pause-reason">Reason (required)</Label>
                    <Textarea
                      id="pause-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. clinic asked to hold outreach during EHR migration"
                      rows={2}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="pause-until">Resume automatically on (optional)</Label>
                    <Input
                      id="pause-until"
                      type="date"
                      value={untilDate}
                      onChange={(e) => setUntilDate(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank to pause until someone resumes it here.
                    </p>
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPending(null)} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  onClick={confirm}
                  disabled={submitting || (pending.pausing && !reason.trim())}
                  variant={pending.pausing ? "destructive" : "default"}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    `${pending.pausing ? "Pause" : "Resume"} ${channelNoun}`
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
